import { useMemo, useState } from "react";
import type { EventoTimelineFCA } from "@/types/fca";

interface Props {
  timeline: EventoTimelineFCA[];
}

const TIPO_LABEL: Record<string, string> = {
  cadastro: "Cadastro",
  alteracao: "Correção",
  vistoria: "Vistoria",
  manejo: "Manejo",
  ocorrencia: "Ocorrência",
  foto: "Foto",
  documento: "Documento",
  outro: "Outro",
};

const TIPO_DOT: Record<string, string> = {
  cadastro: "bg-primary-500",
  alteracao: "bg-danger-500",
  vistoria: "bg-accent-500",
  manejo: "bg-warning-500",
  ocorrencia: "bg-danger-500",
  foto: "bg-caution-500",
  documento: "bg-accent-500",
};

const TIPO_BADGE: Record<string, string> = {
  cadastro: "bg-primary-100 text-primary-700",
  alteracao: "bg-danger-100 text-danger-700",
  vistoria: "bg-accent-100 text-accent-700",
  manejo: "bg-warning-100 text-warning-700",
  ocorrencia: "bg-danger-100 text-danger-700",
  foto: "bg-caution-100 text-caution-700",
};

const formatDate = (dateStr: string) => {
  const [y, m, d] = dateStr.split("-");
  return d && m && y ? `${d}/${m}/${y}` : dateStr;
};

/**
 * Linha do tempo do exemplar: fatos datados e o autor de cada um. Nada é apagado nem
 * reescrito; uma nova vistoria amplia o histórico sem substituir o cadastro.
 */
export default function TreeTimeline({ timeline }: Props) {
  const [tipo, setTipo] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const tipos = useMemo(() => Array.from(new Set(timeline.map((e) => e.tipo))), [timeline]);
  const filtered = useMemo(
    () =>
      timeline.filter((e) => {
        if (tipo && e.tipo !== tipo) return false;
        if (from && e.data < from) return false;
        if (to && e.data > to) return false;
        return true;
      }),
    [timeline, tipo, from, to],
  );

  const inputCls = "px-2.5 py-1.5 rounded-lg border border-background-200 text-xs bg-white";

  return (
    <div>
      <h3 className="text-sm font-semibold text-foreground-900 font-heading mb-3 flex items-center gap-2">
        <i className="ri-history-line text-foreground-600"></i>
        Linha do Tempo
      </h3>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="text-xs text-foreground-600">
          Tipo
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} className={`${inputCls} block mt-1`}>
            <option value="">Todos</option>
            {tipos.map((t) => (
              <option key={t} value={t}>
                {TIPO_LABEL[t] ?? t}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-foreground-600">
          De
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={`${inputCls} block mt-1`} />
        </label>
        <label className="text-xs text-foreground-600">
          Até
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={`${inputCls} block mt-1`} />
        </label>
        {(tipo || from || to) && (
          <button
            type="button"
            onClick={() => {
              setTipo("");
              setFrom("");
              setTo("");
            }}
            className="text-xs text-primary-700 pb-1.5"
          >
            Limpar filtros
          </button>
        )}
      </div>

      <div className="bg-white rounded-card border border-background-200/70 p-5">
        {filtered.length === 0 ? (
          <p className="text-sm text-foreground-500 text-center py-8">
            {timeline.length === 0 ? "Nenhum fato registrado para este exemplar." : "Nenhum fato neste filtro."}
          </p>
        ) : (
          <div className="relative">
            <div className="absolute left-[19px] top-2 bottom-2 w-0.5 bg-background-200"></div>
            <div className="space-y-0">
              {filtered.map((event, idx) => (
                <div key={event.id} className={`relative flex gap-4 ${idx === filtered.length - 1 ? "" : "pb-5"}`}>
                  <div
                    className={`relative z-10 w-[38px] h-[38px] rounded-full ${TIPO_DOT[event.tipo] || "bg-foreground-500"} flex items-center justify-center shrink-0`}
                  >
                    <i className={`${event.icone} text-white text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0 pt-1">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-xs text-foreground-500">{formatDate(event.data)}</span>
                      <span className={`badge text-[10px] ${TIPO_BADGE[event.tipo] ?? "bg-background-100 text-foreground-600"}`}>
                        {TIPO_LABEL[event.tipo] ?? event.tipo}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-foreground-900">{event.titulo}</p>
                    {event.descricao && (
                      <p className="text-xs text-foreground-600 mt-0.5 leading-relaxed">{event.descricao}</p>
                    )}
                    <p className="text-[10px] text-foreground-500 mt-1 flex items-center gap-1">
                      <i className="ri-user-line"></i>
                      {event.tecnico}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
